import {
  Component,
  forwardRef,
  inject,
  input,
  Input,
  OnInit
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NgControl,
  ReactiveFormsModule,
  TouchedChangeEvent,
  ValidationErrors
} from '@angular/forms';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateModule } from '@ngx-translate/core';
import { NgClass } from '@angular/common';
import { Subject } from 'rxjs';
import { filter, startWith } from 'rxjs/operators';
import { AppFormFieldControl } from '../../form-field/form-field-control/form-field-control';
import { SelectOption } from './select-option/select-option';

@UntilDestroy()
@Component({
  selector: 'app-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  providers: [
    {
      provide: AppFormFieldControl,
      useExisting: forwardRef(() => SelectComponent)
    }
  ],
  imports: [FormsModule, NgClass, TranslateModule, ReactiveFormsModule]
})
export class SelectComponent implements OnInit, ControlValueAccessor {
  public ngControl = inject(NgControl, { self: true, optional: true });

  readonly itemLabelKey = input('name');
  readonly itemValueKey = input('value');
  readonly multiple = input(false);
  readonly sharedModel = input<string>(undefined);
  readonly readonly = input(false);
  readonly showAdditionalItem = input(false);
  readonly required = input(false);
  readonly placeholder = input('');
  readonly id = input('');
  readonly count = input(false);

  options: SelectOption[] = [];
  value: unknown = null; // Holds the actual value of the select

  // Needed for AppFormFieldControl to display the error messages
  stateChanges: Subject<void> = new Subject<void>();
  errors: ValidationErrors | null = null;
  touched = false;

  constructor() {
    if (this.ngControl) {
      // Note: we provide the value accessor through here, instead of
      // the `providers` to avoid running into a circular import.
      // Ref: https://github.com/angular/components/blob/main/src/material/select/select.ts#L618
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnInit() {
    if (this.ngControl) {
      // Sync `errors` & `touched` state with the values needed for AppFormFieldControl
      this.ngControl.control.statusChanges
        ?.pipe(
          // Use startWith to set the values initially once
          startWith(null),
          untilDestroyed(this)
        )
        .subscribe(() => {
          this.errors = this.ngControl.errors;
          this.stateChanges.next();
        });
      this.ngControl.control.events
        .pipe(
          // Use startWith to set the values initially once
          startWith(null),
          filter(event => event instanceof TouchedChangeEvent),
          untilDestroyed(this)
        )
        .subscribe(() => {
          this.touched = this.ngControl.control?.touched ?? false;
          this.stateChanges.next();
        });
    }
  }

  // Check if the value is not in the options
  get valueNotAItem() {
    return !this.options.some(option => option.value === this.value);
  }

  // Return an additional item if the value is not an existing option
  get additionalItem() {
    return (
      this.valueNotAItem &&
      new SelectOption(this.value, this.itemLabelKey(), this.itemValueKey())
    );
  }

  @Input()
  set items(items: any[]) {
    this.options =
      items && items.length
        ? items.map(
            (item: any) =>
              new SelectOption(item, this.itemLabelKey(), this.itemValueKey())
          )
        : [];
  }

  shouldAddSharedModelActiveClass() {
    return (
      this.sharedModel() &&
      this.options.some(option => option.value === this.value)
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onChange: (value: unknown) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onTouched: () => void = () => {};

  writeValue(obj: unknown): void {
    this.value = obj; // Only update internal value, don’t trigger change
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  onValueChanges(v: unknown) {
    this.value = v;
    this.onChange(v); // Notify Angular forms system
    this.markAsTouched();
  }

  markAsTouched() {
    this.onTouched(); // Notify Angular that the field was touched
  }
}
